import { takeLatest, all, call, put, select } from "redux-saga/effects";
import { request } from "@app/services";
import {
  product,
  productFailed,
  productSuccess,
  addProduct,
  brands,
  brandsSuccess,
  brandsFailed,
  category,
  categorySuccess,
  categoryFailed,
  findLocation,
  findLocationFailed,
  findLocationSuccess,
  newLocationAdd,
  newLocationAddFailed,
  newLocationAddSuccess,
  productFindById,
  productFindByIdSuccess,
  productFindByIdFailed,
  updateProductById,
  deleteProductById,
  deleteProductByIdSuccess,
  deleteProductByIdFailed,
  addProductSuccess,
  addProductFailed,
  updateProductByIdSuccess,
  updateProductByIdFailed,
  allCars,
  allCarsSuccess,
  allCarsFailed,
  soldCar,
  soldCarSuccess,
  soldCarFailed,
} from "./slice";
import * as endpoints from "./endpoints";
import {
  selectAllProductDataState,
  selectAllCarsDataState,
  selectAllLocationDataState,
} from "./selector";

function* getAllProductsSaga(action: ReturnType<typeof product>): any {
  try {
    const { limit, page, search } = action.payload;
    const response = yield call(
      request.get,
      `${endpoints.allProducts}?search=${search}&limit=${limit}&page=${page}`,
    );
    yield put(productSuccess(response.data));
  } catch (error: any) {
    yield put(productFailed(error));
  }
}
function* getAllCarsSaga(action: ReturnType<typeof allCars>): any {
  try {
    const response = yield call(request.get, endpoints.getAllCarsAdmin);
    yield put(allCarsSuccess(response.data));
  } catch (error: any) {
    yield put(allCarsFailed(error));
  }
}

export function* addProductsSaga({
  payload,
}: ReturnType<typeof addProduct>): any {
  try {
    const { formValues } = payload;
    const formDataToSubmit = formValues as any as FormData;

    const response = yield call(
      request.post,
      `${endpoints.addProducts}`,
      formDataToSubmit,
    );
    yield put(addProductSuccess(response.data));
  } catch (error: any) {
    console.error("API Error:", error.response?.data?.message || error.message);
    yield put(addProductFailed(error.response?.data || error));
  }
}

export function* updateProductByIsSaga({
  payload,
}: ReturnType<typeof updateProductById>): any {
  try {
    const { id, formValues } = payload;
    const formDataToSubmit = formValues as any as FormData;
    const response = yield call(
      request.put,
      `${endpoints.editProduct}/${id}`,
      formDataToSubmit,
      { headers: { "Content-Type": "multipart/form-data" } },
    );

    yield put(updateProductByIdSuccess(response.data));

    const allCarsState = yield select(selectAllProductDataState);
    if (Array.isArray(allCarsState?.data) && allCarsState?.data?.length > 0) {
      const updatedProducts = allCarsState.data.map((item: any) =>
        Number(item.id) === Number(id) ? response.data : item,
      );

      yield put(allCarsSuccess(updatedProducts));
    }
  } catch (error: any) {
    console.error(
      "Update API Error:",
      error.response?.data?.message || error.message,
    );
    yield put(updateProductByIdFailed(error.response?.data || error));
  }
}

function* markSoldSaga({ payload }: ReturnType<typeof soldCar>): any {
  const {
    carId,
    customerId,
    salesPersonId,
    finalPrice,
    paymentMethod,
    invoiceNumber,
    notes,
  }: any = payload;
  try {
    const response = yield call(request.post, endpoints.markAsSold, {
      carId,
      customerId,
      salesPersonId,
      finalPrice,
      paymentMethod,
      invoiceNumber,
      notes,
    });
    yield put(soldCarSuccess(response.data));
  } catch (error: any) {
    console.log("API Error:", error.response?.data?.message || error.message);
    yield put(soldCarFailed(error.response?.data || error));
  }
}

function* getAllBrandsSaga({ payload }: ReturnType<typeof brands>): any {
  try {
    const response = yield call(request.get, `${endpoints.allBrands}`);
    yield put(brandsSuccess(response));
  } catch (error: any) {
    yield put(brandsFailed(error));
  }
}

function* getAllCategorySaga({ payload }: ReturnType<typeof category>): any {
  try {
    const response = yield call(request.get, `${endpoints.allCategory}`);
    yield put(categorySuccess(response));
  } catch (error: any) {
    yield put(categoryFailed(error));
  }
}

//

function* getAllLoationsSaga({
  payload,
}: ReturnType<typeof findLocation>): any {
  try {
    const response = yield call(request.get, `${endpoints.findAllLocation}`);
    yield put(findLocationSuccess(response.data));
  } catch (error: any) {
    yield put(findLocationFailed(error));
  }
}

//

function* productFindByIdSaga(action: ReturnType<typeof productFindById>): any {
  const { productId } = action.payload;
  try {
    const response = yield call(
      request.get,
      `${endpoints.productFindById}/${productId}`,
    );
    yield put(productFindByIdSuccess(response.data));
  } catch (error: any) {
    yield put(productFindByIdFailed(error));
  }
}

function* deleteProductByIdSaga(
  action: ReturnType<typeof deleteProductById>,
): any {
  try {
    const allCarsState = yield select(selectAllCarsDataState);
    const { productId } = action.payload;

    const response = yield call(
      request.delete,
      `${endpoints.deleteProduct}/${productId}`,
    );

    yield put(deleteProductByIdSuccess(response));

    if (Array.isArray(allCarsState?.data) && allCarsState?.data?.length > 0) {
      const newProductsArray = allCarsState.data.filter(
        (item: { id: number }) => item.id !== productId,
      );

      yield put(allCarsSuccess(newProductsArray));
    }
  } catch (error: any) {
    yield put(deleteProductByIdFailed(error));
  }
}
function* addLocationSaga({ payload }: ReturnType<typeof newLocationAdd>): any {
  try {
    const { address, name, city, country, zipCode, lat, long }: any = payload;

    const { data } = yield select(selectAllLocationDataState);

    const response = yield call(request.post, `${endpoints.addNewLocation}`, {
      address,
      name,
      city,
      country,
      zipCode,
      lat,
      long,
    });

    const updatedLocations =
      !Array.isArray(data) || data?.length === 0
        ? [response?.data]
        : [...data, response?.data];
    yield put(newLocationAddSuccess(response.data));
    yield put(findLocationSuccess(updatedLocations));
  } catch (error: any) {
    yield put(newLocationAddFailed(error));
  }
}

function* productsSagas() {
  yield all([
    takeLatest(product, getAllProductsSaga),
    takeLatest(allCars, getAllCarsSaga),
    takeLatest(addProduct, addProductsSaga),
    takeLatest(brands, getAllBrandsSaga),
    takeLatest(category, getAllCategorySaga),
    takeLatest(findLocation, getAllLoationsSaga),
    takeLatest(newLocationAdd, addLocationSaga),
    takeLatest(productFindById, productFindByIdSaga),
    takeLatest(updateProductById, updateProductByIsSaga),
    takeLatest(deleteProductById, deleteProductByIdSaga),
    takeLatest(soldCar, markSoldSaga),
  ]);
}

export { productsSagas };
